import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { db, functions } from '../../../firebase';
import { useAuth } from '../../../hooks/useAuth';
import buildHfpV3ReportModel from '../../utils/hfpV3ReportAdapter';
import buildHfpDisplayModel from '../../components/hfpReport/buildHfpDisplayModel';
import { LEVELS, LEGAL_NOTICE_TEXT } from '../../components/hfpReport/hfpDisplay';
import { resolvePronounSet } from '../../components/hfpReport/pronouns';
import HfpV2Report from '../../components/hfp/HfpV2Report';
import HarbourToast from '../../components/harbour/HarbourToast';
import { hfpInstrumentForRecord } from '../../utils/referralStatus';
import useReleasedPageFrame from '../../hooks/useReleasedPageFrame';
import './hfpPrint.css';

/*
 * Print view of the Holistic Functional Profile (TDI-53 section 30 Download:
 * "Holistic Functional Profile"). Version one of the PDF pipeline: the whole
 * profile — every section, every area, both help lists and the legal notice —
 * rendered as one printable document, with the browser's save-as-PDF as the
 * output. Opens in its own tab from the Download menu and calls print once
 * the written summaries have arrived (or definitively failed, so the
 * deterministic profile still prints rather than hanging).
 *
 * Clinical-safety carriage: the pain-override safety note and the calibration
 * line render verbatim, exactly as the on-screen report carries them.
 */

const levelWord = (level) => {
  if (!level || level.notAsked) return 'Not asked';
  return (LEVELS[level.key] && LEVELS[level.key].word) || null;
};

export default function HfpReportPrintPage() {
  // TDI-794: the profile download shares the answers download's page frame,
  // which cut a saved PDF to its first page.
  useReleasedPageFrame();
  const { patientRecordId } = useParams();
  const { claims } = useAuth();
  const orgId = claims?.rmsOrgId;

  const [record, setRecord] = useState(null);
  const [error, setError] = useState(null);
  const [narratives, setNarratives] = useState(null);
  const [narrativesSettled, setNarrativesSettled] = useState(false);
  const [v2Report, setV2Report] = useState(null);
  const [printed, setPrinted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!orgId || !patientRecordId) return undefined;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'rms_organisations', orgId, 'patientRecords', patientRecordId));
        if (!cancelled) setRecord(snap.exists() ? snap.data() || {} : {});
      } catch (_e) {
        if (!cancelled) setError('The profile could not be loaded. Close this tab and try again.');
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, patientRecordId]);

  const scoring = record?.domains?.hfpV3?.scoring || null;
  // Only a v2 record falls back to the v2 profile (Harbour pupils and stored
  // v3/v4 records never do).
  const v2Fallback = Boolean(record) && hfpInstrumentForRecord(record) === 'v2';
  const childName = record?.subject?.firstName || record?.subject_first_name || 'the child';

  // The child's pronoun set, resolved exactly as the on-screen report does.
  const [pronounSet, setPronounSet] = useState('they/them');
  useEffect(() => {
    let cancelled = false;
    if (!record) return undefined;
    resolvePronounSet(orgId, patientRecordId, record).then((set) => {
      if (!cancelled && set) setPronounSet(set);
    });
    return () => { cancelled = true; };
  }, [record, orgId, patientRecordId]);

  // v3: fetch the written summaries once; the profile prints with or without
  // them, but never before the callable has settled.
  const requestedRef = useRef(false);
  useEffect(() => {
    if (!record || !scoring || requestedRef.current) return;
    requestedRef.current = true;
    (async () => {
      try {
        const fn = httpsCallable(functions, 'rmsGenerateHfpV3Report', { timeout: 540000 });
        const res = await fn({ orgId, patientRecordId });
        if (res?.data?.narratives) setNarratives(res.data.narratives);
      } catch (_e) {
        // The deterministic profile still prints.
      } finally {
        setNarrativesSettled(true);
      }
    })();
  }, [record, scoring, orgId, patientRecordId]);

  // v2 (legacy records only — Harbour pupils never fall back).
  useEffect(() => {
    if (!record || scoring || !v2Fallback || v2Report) return;
    (async () => {
      try {
        const fn = httpsCallable(functions, 'rmsGenerateHfpV2Profile', { timeout: 540000 });
        const res = await fn({ orgId, patientRecordId, mode: 'llm' });
        setV2Report(res?.data?.report || null);
        if (!res?.data?.report) setError('The profile is not ready yet.');
      } catch (_e) {
        setError('The profile could not be loaded. Close this tab and try again.');
      }
    })();
  }, [record, scoring, v2Fallback, v2Report, orgId, patientRecordId]);

  const display = useMemo(() => {
    if (!scoring) return null;
    const model = buildHfpV3ReportModel(scoring, childName, narratives);
    return buildHfpDisplayModel(model, childName, pronounSet, { narrativesArrived: Boolean(narratives) });
  }, [scoring, childName, narratives, pronounSet]);

  // Print once the content is final; the toast confirms after the dialog.
  const ready = Boolean((display && narrativesSettled) || v2Report);
  useEffect(() => {
    if (!ready) return undefined;
    const onAfterPrint = () => setPrinted(true);
    window.addEventListener('afterprint', onAfterPrint);
    const timer = setTimeout(() => window.print(), 400);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('afterprint', onAfterPrint);
    };
  }, [ready]);

  if (error) {
    return <p className="hfp-print__status" role="alert">{error}</p>;
  }
  // No v3 profile and no v2 fallback: nothing to print yet.
  if (record && !scoring && !v2Fallback) {
    return <p className="hfp-print__status" role="status">The profile is not ready yet.</p>;
  }
  if (!ready) {
    return (
      <p className="hfp-print__status" role="status">
        Preparing the profile for download — this can take a minute or two the first time.
      </p>
    );
  }

  if (!display && v2Report) {
    return (
      <div className="hfp-print">
        <HfpV2Report report={v2Report} childName={childName} pageMode />
        <p className="hfp-print__legal">{LEGAL_NOTICE_TEXT}</p>
        {printed && (
          <HarbourToast title="PDF downloaded" onClose={() => setPrinted(false)}>
            {`${childName}’s profile has been saved from the print dialog.`}
          </HarbourToast>
        )}
      </div>
    );
  }

  const helpList = (setting, heading) => (
    <section className="hfp-print__section">
      <h2>{heading}</h2>
      <p className="hfp-print__lede">{display.helpLedes[setting]}</p>
      {display.help[setting].all.length === 0 && <p>No ideas to show for this setting.</p>}
      {display.help[setting].all.map((group) => (
        <div key={`${setting}-${group.areaName}`} className="hfp-print__area">
          <h3>
            {group.areaName}
            {group.level && LEVELS[group.level] ? ` — ${LEVELS[group.level].word}` : ''}
          </h3>
          {group.whyMatters && <p>{group.whyMatters}</p>}
          {group.impactBullets.length > 0 && (
            <ul>{group.impactBullets.map((b) => <li key={b}>{b}</li>)}</ul>
          )}
          {group.adjustments.map((item) => (
            <div key={item.number} className="hfp-print__adjustment">
              <p>
                <strong>{`${item.number}. ${item.title}`}</strong>
                {item.category ? ` (${item.category})` : ''}
              </p>
              {item.body && item.body !== item.title && <p>{item.body}</p>}
              {item.bullets.length > 0 && (
                <ul>{item.bullets.map((b) => <li key={b}>{b}</li>)}</ul>
              )}
            </div>
          ))}
        </div>
      ))}
    </section>
  );

  return (
    <div className="hfp-print">
      <header className="hfp-print__head">
        <h1>{`${display.childName}’s strengths and needs profile`}</h1>
        <p className="hfp-print__legal">{LEGAL_NOTICE_TEXT}</p>
        {display.calibrationNote && <p className="hfp-print__legal">{display.calibrationNote}</p>}
      </header>

      {display.sections.map((section) => (
        <section key={section.key} className="hfp-print__section">
          <h2>{section.title}</h2>
          <p className="hfp-print__lede">{section.lede}</p>
          {section.internal === 'Sensory' && display.painSafety && (
            <p className="hfp-print__safety">
              <strong>Safety note:</strong>{' '}
              {`${display.childName} may not notice pain the way other children do; worth everyone knowing, so injuries and illness are not missed.`}
            </p>
          )}
          {section.areas.map((area) => (
            <div key={area.id} className="hfp-print__area">
              <h3>{area.name}</h3>
              <p className="hfp-print__levels">
                {[
                  levelWord(area.home) && `Home: ${levelWord(area.home)}`,
                  levelWord(area.school) && `School: ${levelWord(area.school)}`,
                ].filter(Boolean).join(' · ')}
              </p>
              {area.story && <p>{area.story}</p>}
            </div>
          ))}
        </section>
      ))}

      {helpList('home', 'What could help at home')}
      {helpList('school', 'What could help at school')}

      <footer className="hfp-print__foot">
        <p className="hfp-print__legal">{LEGAL_NOTICE_TEXT}</p>
      </footer>

      {printed && (
        <HarbourToast title="PDF downloaded" onClose={() => setPrinted(false)}>
          {`${display.childName}’s profile has been saved from the print dialog.`}
        </HarbourToast>
      )}
    </div>
  );
}
